import { Card, Flex } from "antd";
import React, { useMemo } from "react";
import { DryerSettingsText } from "./DryerSettingsText";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { formatNumber } from "src/utils/formatting";
import { getGrainTypeStr } from "src/utils/enumUtils";
import {
  convertFahrenheitToCelsius,
  convertFahrenheitToCelsiusOffset,
} from "src/utils/generalUtils";

interface ReconditioningSettingsCardProps {
  binDto: PremierPlusBinDTO | null | undefined;
}

export const ReconditioningSettingsCard: React.FC<ReconditioningSettingsCardProps> = ({ binDto }) => {

  const reconditioningSettings = useMemo(
    () => binDto?.settings?.reconditioningSettings,
    [binDto]
  );

  const useCelsius = binDto?.platformSettings?.useMetricSystem === true;

  const unit = useMemo(() => (useCelsius ? "°C" : "°F"), [useCelsius]);

  const toDisplayTemp = useMemo(() => {
    return (value?: number | null) => {
      if (value == null) return value;
      return useCelsius ? convertFahrenheitToCelsius(value) : value;
    };
  }, [useCelsius]);

  const toDisplayOffset = useMemo(() => {
    return (value?: number | null) => {
      if (value == null) return value;
      return useCelsius ? convertFahrenheitToCelsiusOffset(value) : value;
    };
  }, [useCelsius]);

  const text = useMemo(() => {
    const lines: string[] = [];

    lines.push(
      `Target Moisture=${formatNumber(reconditioningSettings?.targetMcPercent, {
        decimalPlaces: 1,
        suffix: "%",
        filler: "--",
      })}`
    );

    lines.push(
      `Number of samples in avg=${reconditioningSettings?.samplesCntInAverage ?? "--"}`
    );

    lines.push(
      `Use Fan Timer=${reconditioningSettings?.useTimer === true ? "Yes" : "No"}`
    );

    if (reconditioningSettings?.useTimer) {
      lines.push(
        `Fan Duration=${reconditioningSettings?.runFanHours ?? 0}hr ${reconditioningSettings?.runFanMinutes ?? 0}min`
      );
    }

    lines.push(
      `Use EMC Set Point=${reconditioningSettings?.useEmcRange === true ? "Yes" : "No"}`
    );

    if (reconditioningSettings?.useEmcRange) {
      lines.push(
        `EMC Range=${formatNumber(reconditioningSettings?.minWeatherMc, { decimalPlaces: 1, suffix: "%", filler: "--" })} - ${formatNumber(reconditioningSettings?.maxWeatherMc, { decimalPlaces: 1, suffix: "%", filler: "--" })}`
      );
    }

    lines.push(
      `Use Temp Set Point=${reconditioningSettings?.useTempRange === true ? "Yes" : "No"}`
    );

    if (reconditioningSettings?.useTempRange) {
      lines.push(
        `Temp Range=${formatNumber(toDisplayTemp(reconditioningSettings?.minWeatherTemperatureF), { decimalPlaces: 1, suffix: unit, filler: "--" })} - ${formatNumber(toDisplayTemp(reconditioningSettings?.maxWeatherTemperatureF), { decimalPlaces: 1, suffix: unit, filler: "--" })}`
      );
    }

    lines.push(
      `Grain Temp Cal=${formatNumber(toDisplayOffset(reconditioningSettings?.grainTempCalibrationF), { decimalPlaces: 1, suffix: unit, filler: "--" })}`
    );

    lines.push(
      `Moisture Cal=${formatNumber(reconditioningSettings?.grainMcCalibrationPercent, { decimalPlaces: 1, suffix: "%", filler: "--" })}`
    );

    lines.push(
      `Plenum Temp Cal=${formatNumber(toDisplayOffset(reconditioningSettings?.plenumTempCalibrationF), { decimalPlaces: 1, suffix: unit, filler: "--" })}`
    );

    lines.push(
      `Check Moist Change=${reconditioningSettings?.disableMoistureChangingCheck === true ? "No" : "Yes"}`
    );

    lines.push(`Grain Type=${getGrainTypeStr(binDto?.settings?.dryerSettings?.grainType)}`);
    lines.push(`Bin Name=${binDto?.binName}`);
    lines.push(
      `Version=${binDto?.platformState?.platformVersion}-${binDto?.platformState?.hmiVersion}`
    );

    return lines.join("\r\n");
  }, [binDto, reconditioningSettings, toDisplayTemp, toDisplayOffset, unit]);

  return (
    <Card
      loading={false}
      title="Reconditioning Settings"
      style={{ width: "100%" }}
      styles={{
        body: {
          padding: 0,
          minWidth: "296px",
          width: "100%",
          minHeight: "504px",
        },
      }}
    >
      <div style={{ width: "100%" }}>
        <Flex justify="center" style={{ width: "100%" }}>
          <DryerSettingsText text={text} />
        </Flex>
      </div>
    </Card>
  );
};
